import { ThemeEvents } from '@theme/events';

/**
 * VEYR — AURELIS "Ball Court" colour selector.
 *
 * One physical row of tennis balls rolls to the chosen colour. Moving up the
 * list plays a forward master; moving down plays a physically reversed master.
 * Both files share one logical timeline (t_reverse = duration - t_forward), so
 * an interrupted transition can be re-aimed from the exact current frame
 * instead of jumping to the nearest anchor.
 *
 * Cart mutations are delegated to the theme's own <product-form-component>;
 * this module never posts to /cart/add itself.
 */

const CART_UPDATE = ThemeEvents?.cartUpdate ?? 'cart:update';
const CART_ERROR = ThemeEvents?.cartError ?? 'cart:error';

/** Land within one 60fps frame of the measured anchor. */
const ANCHOR_EPSILON = 0.02;
/** Fraction of the track that confirms an add. */
const SLIDE_THRESHOLD = 0.88;
/** Minimum horizontal travel that counts as a swipe. */
const SWIPE_DISTANCE = 40;
/** product-form.js can swallow a failed fetch; release the control anyway. */
const ADD_WATCHDOG = 10000;
const RESET_DELAY = 1800;

/**
 * Travel speed. A neighbouring colour is worth watching, so it runs close to
 * real time; a jump across the whole row would otherwise crawl, so it runs
 * far faster. Wall-clock time therefore grows only gently with distance:
 * roughly 1.1 s for one step and 2.6 s for all six, instead of 1.8 s and 12 s.
 */
const TRANSIT_BASE = 0.95;
const TRANSIT_CURVE = 0.32;
const TRANSIT_MIN = 0.9;
const TRANSIT_MAX = 2.2;
const RATE_MAX = 6;
/** The last stretch always eases back to near real time, so it lands calmly. */
const LANDING_RATE = 1.15;
const LANDING_WINDOW = 0.7;

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const reducedMotion = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

class VeyrAurelisBallCourt extends HTMLElement {
  #abort = new AbortController();
  #observer = null;
  #visibility = null;

  /** @type {Array<{label:string,optionValue:string,swatch:string,anchor:number,poster:string}>} */
  #states = [];
  /** @type {Array<{id:number,colour:string,size:string,available:boolean,price:string,url:string}>} */
  #variants = [];
  #media = {};

  #index = 0;
  #desired = 0;
  #token = 0;
  #running = false;
  #state = 'idle';

  #duration = 12.45;
  #videoFwd = null;
  #videoRev = null;
  #activeDir = 'forward';
  #hydrated = false;
  #reverseHydrated = false;
  #videoFailed = false;
  #posterOnly = false;
  #frameHandle = null;
  #rafHandle = 0;
  #proximityCheck = null;
  #proximityTimer = 0;
  #proximityRaf = 0;

  #size = null;
  #variant = null;

  #slideDragging = false;
  #slideProgress = 0;
  #slideOrigin = 0;
  #travelCache = 0;
  #didDrag = false;
  #dragEndedAt = 0;
  #addInFlight = false;
  #watchdog = 0;
  #resetTimer = 0;
  #swipe = null;

  connectedCallback() {
    // A theme-editor re-render can reconnect the same element; start clean.
    if (this.#abort.signal.aborted) this.#abort = new AbortController();

    this.#readData();
    if (!this.#states.length) return;

    this.#cache();
    this.#posterOnly = reducedMotion();
    if (this.#posterOnly) this.dataset.posterOnly = 'true';

    this.#index = clamp(Number(this.dataset.startIndex) || 0, 0, this.#states.length - 1);
    this.#desired = this.#index;

    // On a product template Liquid has already resolved ?variant=; mirror it.
    const checked = this.querySelector('[data-veyr-abc-swatch][aria-checked="true"]');
    if (checked) {
      const i = Number(checked.dataset.index);
      if (Number.isInteger(i)) this.#index = this.#desired = i;
    }
    this.#size = this.#currentSizeFromMarkup();

    // Elsewhere — the home page, where the product comes from a section setting
    // — Shopify does not bind ?variant= to it, so read the URL ourselves. This
    // makes the address this section writes survive a reload on any template.
    const deepLinked = this.#stateFromUrl();
    if (deepLinked) {
      this.#index = this.#desired = deepLinked.index;
      this.#size = deepLinked.size || this.#size;
    }

    this.#variant = this.#resolveVariant(this.#states[this.#index]?.optionValue, this.#size);

    this.#bind();
    this.#enhance();
    this.#commit(this.#index, { announce: false, pushUrl: false });
    this.#setState('settled');

    if (!this.#posterOnly) this.#watchViewport();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.#observer?.disconnect();
    this.#visibility?.disconnect();
    this.#disarmProximityFallback();
    this.#cancelFrameWatch();
    clearTimeout(this.#watchdog);
    clearTimeout(this.#resetTimer);
    for (const v of [this.#videoFwd, this.#videoRev]) {
      if (!v) continue;
      try {
        v.pause();
        v.playbackRate = 1;
        v.removeAttribute('src');
        v.load();
      } catch {
        /* teardown is best effort */
      }
    }
    // The theme morphs sections in place, so this very instance can be
    // reconnected later. Having released the sources, forget that we loaded
    // them, otherwise the film would never come back.
    this.#hydrated = false;
    this.#reverseHydrated = false;
    this.#running = false;
    this.#addInFlight = false;
    this.#token += 1;
  }

  /* ---------------------------------------------------------------- data */

  #readData() {
    const parse = (sel, fallback) => {
      const node = this.querySelector(sel);
      if (!node) return fallback;
      try {
        return JSON.parse(node.textContent || '');
      } catch {
        return fallback;
      }
    };
    this.#states = parse('[data-veyr-abc-states]', []).filter((s) => s && s.label);
    this.#variants = parse('[data-veyr-abc-variants]', []);
    this.#media = parse('[data-veyr-abc-media]', {});
    const d = Number(this.dataset.duration);
    if (Number.isFinite(d) && d > 0) this.#duration = d;
  }

  #cache() {
    this.$ = {
      frame: this.querySelector('[data-veyr-abc-frame]'),
      poster: this.querySelector('[data-veyr-abc-poster]'),
      price: this.querySelector('[data-veyr-abc-price]'),
      colourName: this.querySelector('[data-veyr-abc-colour-name]'),
      swatches: [...this.querySelectorAll('[data-veyr-abc-swatch]')],
      steps: [...this.querySelectorAll('[data-veyr-abc-steps] .veyr-abc__step')],
      sizes: [...this.querySelectorAll('[data-veyr-abc-size]')],
      slide: this.querySelector('[data-veyr-abc-slide]'),
      track: this.querySelector('[data-veyr-abc-track]'),
      handle: this.querySelector('[data-veyr-abc-handle]'),
      slideText: this.querySelector('[data-veyr-abc-slide-text]'),
      status: this.querySelector('[data-veyr-abc-status]'),
      prev: this.querySelector('[data-veyr-abc-prev]'),
      next: this.querySelector('[data-veyr-abc-next]'),
      form: this.querySelector('product-form-component'),
      variantInput: this.querySelector('[data-veyr-abc-variant-input]'),
    };
    this.#videoFwd = this.querySelector('[data-veyr-abc-video="forward"]');
    this.#videoRev = this.querySelector('[data-veyr-abc-video="reverse"]');
  }

  #currentSizeFromMarkup() {
    const active = this.$.sizes.find((b) => b.getAttribute('aria-checked') === 'true');
    return active ? active.dataset.value : null;
  }

  /** Resolves ?variant= against this product's own variants, or null. */
  #stateFromUrl() {
    let id = null;
    try {
      id = new URL(window.location.href).searchParams.get('variant');
    } catch {
      return null;
    }
    if (!id) return null;
    const variant = this.#variants.find((v) => String(v.id) === String(id));
    if (!variant) return null;
    const index = this.#states.findIndex((s) => s.optionValue && s.optionValue === variant.colour);
    if (index < 0) return null;
    return { index, size: variant.size };
  }

  /* ------------------------------------------------------------- binding */

  #bind() {
    const { signal } = this.#abort;
    const on = (el, type, fn, opts) => el?.addEventListener(type, fn, { signal, ...opts });

    for (const sw of this.$.swatches) {
      on(sw, 'click', (e) => {
        e.preventDefault();
        this.goTo(Number(sw.dataset.index));
      });
    }
    on(this.querySelector('[data-veyr-abc-swatches]'), 'keydown', this.#onSwatchKeys);

    for (const btn of this.$.sizes) {
      on(btn, 'click', (e) => {
        e.preventDefault();
        this.#selectSize(btn.dataset.value);
      });
    }
    on(this.querySelector('.veyr-abc__sizes-row'), 'keydown', this.#onSizeKeys);

    on(this.$.prev, 'click', () => this.goTo(this.#desired - 1));
    on(this.$.next, 'click', () => this.goTo(this.#desired + 1));

    on(this.$.frame, 'pointerdown', this.#onSwipeStart);
    on(this.$.frame, 'pointermove', this.#onSwipeMove);
    on(this.$.frame, 'pointerup', this.#onSwipeEnd);
    on(this.$.frame, 'pointercancel', this.#onSwipeCancel);

    on(this.$.handle, 'pointerdown', this.#onSlideStart);
    on(this.$.handle, 'pointermove', this.#onSlideMove);
    on(this.$.handle, 'pointerup', this.#onSlideEnd);
    on(this.$.handle, 'pointercancel', this.#onSlideCancel);
    on(this.$.handle, 'click', this.#onSlideClick);

    on(this, CART_UPDATE, this.#onCartUpdate);
    on(this, CART_ERROR, this.#onCartError);

    for (const v of [this.#videoFwd, this.#videoRev]) {
      on(v, 'error', () => this.#failVideo());
    }

    // Theme editor: selecting a colour block brings that state to the light.
    on(document, 'shopify:block:select', (e) => {
      if (!this.contains(e.target) && e.target !== this) return;
      const el = e.target?.closest?.('[data-veyr-abc-swatch]');
      if (el) this.goTo(Number(el.dataset.index));
    });
  }

  #enhance() {
    if (!this.$.slide || !this.$.handle) return;
    this.dataset.enhanced = 'true';
    this.$.slide.hidden = false;
    if (this.#posterOnly && this.$.slideText) {
      this.$.slideText.textContent = 'ADD TO CART';
    }
  }

  /* ------------------------------------------------------------ viewport */

  #watchViewport() {
    if (!('IntersectionObserver' in window)) {
      this.#armProximityFallback();
      return;
    }
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) this.#hydrate();
      },
      { rootMargin: '400px 0px' }
    );
    this.#observer.observe(this);

    // A page that is never painted (background tab, prerender, some in-app
    // webviews) may never receive an IntersectionObserver record. Keep a cheap
    // proximity check as a second route so the film still arrives.
    this.#armProximityFallback();

    this.#visibility = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) continue;
          this.#videoFwd?.pause();
          this.#videoRev?.pause();
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.#visibility.observe(this);
  }

  #armProximityFallback() {
    if (this.#hydrated || this.#posterOnly) return;
    const check = () => {
      if (this.#hydrated) return this.#disarmProximityFallback();
      const r = this.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight || 0;
      if (r.bottom > -400 && r.top < vh + 400) this.#hydrate();
    };
    this.#proximityCheck = () => {
      if (this.#proximityRaf) return;
      this.#proximityRaf = requestAnimationFrame(() => {
        this.#proximityRaf = 0;
        check();
      });
    };
    const opts = { passive: true, signal: this.#abort.signal };
    window.addEventListener('scroll', this.#proximityCheck, opts);
    window.addEventListener('resize', this.#proximityCheck, opts);
    this.#proximityTimer = window.setTimeout(check, 600);
  }

  #disarmProximityFallback() {
    clearTimeout(this.#proximityTimer);
    if (this.#proximityRaf) cancelAnimationFrame(this.#proximityRaf);
    this.#proximityRaf = 0;
    if (this.#proximityCheck) {
      window.removeEventListener('scroll', this.#proximityCheck);
      window.removeEventListener('resize', this.#proximityCheck);
      this.#proximityCheck = null;
    }
  }

  #wantsMobileEncode() {
    return window.matchMedia?.('(max-width: 749px)').matches ?? false;
  }

  #hydrate() {
    if (this.#hydrated || this.#posterOnly) return;
    this.#observer?.disconnect();
    this.#observer = null;
    this.#disarmProximityFallback();
    const mobile = this.#wantsMobileEncode();
    const fwd = (mobile && this.#media.forwardMobile) || this.#media.forward;
    if (!fwd || !this.#videoFwd) {
      this.#failVideo();
      return;
    }
    this.#hydrated = true;
    this.#videoFwd.preload = 'auto';
    this.#videoFwd.src = fwd;
    this.#videoFwd.load();
    this.#videoFwd.addEventListener(
      'loadeddata',
      () => {
        this.#seek(this.#videoFwd, this.#anchor(this.#index, 'forward'));
        // The reverse master is only worth its bytes once the forward one is
        // usable; on a slow link the first backward move fetches it on demand.
        const idle = window.requestIdleCallback ?? ((fn) => setTimeout(fn, 1200));
        idle(() => this.#hydrateReverse());
      },
      { once: true, signal: this.#abort.signal }
    );
  }

  #hydrateReverse() {
    if (this.#reverseHydrated || this.#posterOnly || this.#videoFailed) return;
    const mobile = this.#wantsMobileEncode();
    const rev = (mobile && this.#media.reverseMobile) || this.#media.reverse;
    if (!rev || !this.#videoRev) return;
    this.#reverseHydrated = true;
    this.#videoRev.preload = 'auto';
    this.#videoRev.src = rev;
    this.#videoRev.load();
  }

  #failVideo() {
    this.#videoFailed = true;
    this.#posterOnly = true;
    this.dataset.posterOnly = 'true';
  }

  /* ---------------------------------------------------------- transitions */

  #anchor(i, dir) {
    const a = Number(this.#states[i]?.anchor) || 0;
    return dir === 'forward' ? a : this.#duration - a;
  }

  /** Current position expressed on the shared forward timeline. */
  #timelinePosition() {
    const v = this.#activeDir === 'forward' ? this.#videoFwd : this.#videoRev;
    if (!v || !v.currentSrc || !Number.isFinite(v.currentTime)) {
      return this.#anchor(this.#index, 'forward');
    }
    return this.#activeDir === 'forward' ? v.currentTime : this.#duration - v.currentTime;
  }

  goTo(next) {
    if (!this.#states.length) return;
    // Someone is choosing a colour: the film is needed now, whatever the
    // viewport observers have or have not reported.
    this.#hydrate();
    const target = clamp(next, 0, this.#states.length - 1);
    if (target === this.#desired && (this.#running || target === this.#index)) return;
    this.#desired = target;
    // Invalidate any in-flight frame watcher so it stops committing.
    this.#token += 1;
    this.#run();
  }

  async #run() {
    if (this.#running) return;
    this.#running = true;
    try {
      while (this.#desired !== this.#index) {
        const token = this.#token;
        const target = this.#desired;
        this.#setState('animating');
        this.#markPending(target);
        const played = await this.#transition(target, token);
        if (token !== this.#token) continue; // re-aimed mid-roll; loop again
        // If the film could not carry us there, park the timeline on the
        // target anchor so the next move still computes from a truthful frame.
        if (!played) await this.#syncToAnchor(target);
        this.#index = target;
        this.#commit(target);
      }
      this.#clearPending();
      this.#setState('settled');
    } finally {
      this.#running = false;
    }
  }

  async #transition(target, token) {
    if (this.#posterOnly || this.#videoFailed) return false;

    const fromT = this.#timelinePosition();
    const toT = this.#anchor(target, 'forward');
    if (Math.abs(toT - fromT) < ANCHOR_EPSILON) return true;

    const forward = toT > fromT;
    if (!forward) this.#hydrateReverse();

    const video = forward ? this.#videoFwd : this.#videoRev;
    const other = forward ? this.#videoRev : this.#videoFwd;
    if (!video || !video.getAttribute('src')) {
      // The needed master never arrived — land on the poster instead.
      return false;
    }

    const start = forward ? fromT : this.#duration - fromT;
    const end = forward ? toT : this.#duration - toT;

    try {
      await this.#ready(video);
      if (token !== this.#token) return false;

      if (!this.#canSeek(video)) {
        // Without seeking there is no honest way to start on an anchor.
        this.#failVideo();
        return false;
      }

      const landed = await this.#seek(video, start);
      if (token !== this.#token) return false;
      if (!landed) {
        this.#failVideo();
        return false;
      }

      this.#activeDir = forward ? 'forward' : 'reverse';
      video.classList.add('is-active');
      other?.classList.remove('is-active');

      // Far colours arrive fast, neighbours stay watchable.
      const distance = Math.abs(end - start);
      video.playbackRate = this.#rateFor(distance);
      const easeAt = end - Math.min(LANDING_WINDOW, distance * 0.25);

      await video.play().catch(() => {});
      if (token !== this.#token) {
        video.pause();
        video.playbackRate = 1;
        return false;
      }

      await this.#playUntil(video, end, token, easeAt);
      video.pause();
      video.playbackRate = 1;
      if (token !== this.#token) return false;
      await this.#seek(video, end);
      return true;
    } catch {
      this.#failVideo();
      return false;
    }
  }

  /**
   * Playback rate for a jump of `distance` media seconds. Target wall-clock
   * time rises as distance^0.32, so crossing the whole row costs a little more
   * time than one step, not seven times more.
   */
  #rateFor(distance) {
    if (!(distance > 0)) return 1;
    const target = clamp(TRANSIT_BASE * Math.pow(distance, TRANSIT_CURVE), TRANSIT_MIN, TRANSIT_MAX);
    return clamp(distance / target, 1, RATE_MAX);
  }

  /** Park the active master exactly on a state's anchor without playing. */
  async #syncToAnchor(index) {
    const video = this.#activeDir === 'forward' ? this.#videoFwd : this.#videoRev;
    if (!video || !video.getAttribute('src')) return;
    await this.#seek(video, this.#anchor(index, this.#activeDir));
  }

  #ready(video) {
    if (video.readyState >= 2) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const t = setTimeout(() => reject(new Error('video stalled')), 8000);
      video.addEventListener(
        'loadeddata',
        () => {
          clearTimeout(t);
          resolve();
        },
        { once: true, signal: this.#abort.signal }
      );
    });
  }

  /** True when the browser will honour a jump to an arbitrary time. */
  #canSeek(video) {
    const s = video?.seekable;
    if (!s || s.length === 0) return false;
    return s.end(s.length - 1) - s.start(0) > 0.5;
  }

  #seekOnce(video, target) {
    return new Promise((resolve) => {
      const done = () => {
        clearTimeout(t);
        resolve();
      };
      const t = setTimeout(done, 2000);
      video.addEventListener('seeked', done, { once: true, signal: this.#abort.signal });
      try {
        video.currentTime = target;
      } catch {
        done();
      }
    });
  }

  /**
   * Seeks and then checks where we actually landed. A silently refused seek
   * would otherwise start playback from the wrong frame, which reads as the
   * row jumping — worse than not moving at all.
   * @returns {Promise<boolean>} whether the film is on the requested frame.
   */
  async #seek(video, time) {
    if (!video) return false;
    const target = clamp(time, 0, Math.max(0, (video.duration || this.#duration) - 0.001));
    if (Math.abs(video.currentTime - target) < 0.02) return true;

    await this.#seekOnce(video, target);
    if (Math.abs(video.currentTime - target) <= 0.15) return true;

    // One retry covers a seek issued a fraction too early after load.
    await this.#seekOnce(video, target);
    return Math.abs(video.currentTime - target) <= 0.15;
  }

  /**
   * Stops on the measured anchor. requestVideoFrameCallback gives frame-exact
   * detection where available; otherwise a rAF loop reads currentTime.
   */
  #playUntil(video, end, token, easeAt = end) {
    return new Promise((resolve) => {
      const rate = Math.max(1, video.playbackRate || 1);
      const budget = Math.max(1200, ((end - video.currentTime) / rate) * 1000 + 2500);
      const bail = setTimeout(finish, budget);

      // The ease is also scheduled on the clock, not only on frame callbacks:
      // a throttled or backgrounded tab delivers too few callbacks to catch
      // the window, and the landing would then stop at full speed.
      const easeDelay = Math.max(0, ((easeAt - video.currentTime) / rate) * 1000);
      const easeTimer = setTimeout(() => {
        if (token === this.#token && video.playbackRate > LANDING_RATE) {
          video.playbackRate = LANDING_RATE;
        }
      }, easeDelay);

      const step = () => {
        if (token !== this.#token) {
          // Re-aimed mid-roll: stop the film on this frame right now, so the
          // new transition starts from exactly where the eye last saw it.
          video.pause();
          video.playbackRate = 1;
          return finish();
        }
        const now = video.currentTime;
        // Ease back to near real time before the anchor: it reads as a landing
        // rather than a stop, and it keeps the final frame accurate at speed.
        if (now >= easeAt && video.playbackRate > LANDING_RATE) {
          video.playbackRate = LANDING_RATE;
        }
        if (video.paused && now < end - ANCHOR_EPSILON) return finish();
        if (now >= end - ANCHOR_EPSILON || video.ended) return finish();
        schedule();
      };

      const schedule = () => {
        if (typeof video.requestVideoFrameCallback === 'function') {
          this.#frameHandle = video.requestVideoFrameCallback(step);
        } else {
          this.#rafHandle = requestAnimationFrame(step);
        }
      };

      function finish() {
        clearTimeout(bail);
        clearTimeout(easeTimer);
        resolve();
      }

      this.#cancelFrameWatch();
      schedule();
    }).finally(() => this.#cancelFrameWatch());
  }

  #cancelFrameWatch() {
    const v = this.#activeDir === 'forward' ? this.#videoFwd : this.#videoRev;
    if (this.#frameHandle && typeof v?.cancelVideoFrameCallback === 'function') {
      v.cancelVideoFrameCallback(this.#frameHandle);
    }
    this.#frameHandle = null;
    if (this.#rafHandle) cancelAnimationFrame(this.#rafHandle);
    this.#rafHandle = 0;
  }

  /* -------------------------------------------------------------- commit */

  #markPending(target) {
    for (const sw of this.$.swatches) {
      sw.dataset.pending = String(Number(sw.dataset.index) === target);
    }
    this.#setSlideEnabled(false);
  }

  #clearPending() {
    for (const sw of this.$.swatches) delete sw.dataset.pending;
  }

  #commit(index, { announce = true, pushUrl = true } = {}) {
    const state = this.#states[index];
    if (!state) return;

    this.#clearPending();

    for (const sw of this.$.swatches) {
      const active = Number(sw.dataset.index) === index;
      sw.setAttribute('aria-checked', String(active));
      sw.tabIndex = active ? 0 : -1;
    }
    this.$.steps.forEach((step, i) => {
      step.dataset.active = String(i === index);
      step.dataset.passed = String(i < index);
    });
    if (this.$.colourName) this.$.colourName.textContent = state.label;
    if (this.$.poster && state.poster) this.$.poster.src = state.poster;
    this.#preloadNeighbourPosters(index);

    if (this.$.prev) this.$.prev.disabled = index === 0;
    if (this.$.next) this.$.next.disabled = index === this.#states.length - 1;

    const previousSize = this.#size;
    const variant = this.#resolveVariant(state.optionValue, this.#size);
    this.#variant = variant;
    if (variant) this.#size = variant.size || this.#size;

    this.#syncSizes(state.optionValue);

    if (this.$.price) {
      this.$.price.textContent = variant ? variant.price : this.$.price.textContent;
    }
    if (this.$.variantInput && variant) this.$.variantInput.value = String(variant.id);

    if (pushUrl && variant) this.#writeUrl(variant.id);

    this.#refreshCta(state, variant, previousSize);
    if (announce) this.#announce(state, variant, previousSize);
  }

  #resolveVariant(colourValue, preferredSize) {
    if (!colourValue) return null;
    const pool = this.#variants.filter((v) => v.colour === colourValue);
    if (!pool.length) return null;
    if (preferredSize) {
      const exact = pool.find((v) => v.size === preferredSize);
      if (exact) return exact;
    }
    return pool.find((v) => v.available) || pool[0];
  }

  #syncSizes(colourValue) {
    for (const btn of this.$.sizes) {
      const value = btn.dataset.value;
      const match = this.#variants.find((v) => v.colour === colourValue && v.size === value);
      const active = value === this.#size;
      btn.setAttribute('aria-checked', String(active));
      btn.tabIndex = active ? 0 : -1;
      btn.dataset.unavailable = String(!match || !match.available);
      if (match) {
        btn.removeAttribute('aria-disabled');
        if (match.url) btn.setAttribute('href', match.url);
      } else {
        btn.setAttribute('aria-disabled', 'true');
      }
    }
  }

  #selectSize(value) {
    if (!value) return;
    const state = this.#states[this.#index];
    const exists = this.#variants.some(
      (v) => v.colour === state?.optionValue && v.size === value
    );
    if (!exists) return; // this colour is not made in that size
    this.#size = value;
    this.#commit(this.#index);
  }

  #preloadNeighbourPosters(index) {
    for (const i of [index - 1, index + 1]) {
      const url = this.#states[i]?.poster;
      if (!url) continue;
      const img = new Image();
      img.decoding = 'async';
      img.src = url;
    }
  }

  #writeUrl(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variantId));
      window.history.replaceState(window.history.state, '', url.toString());
    } catch {
      /* a blocked history write must never break the selector */
    }
  }

  #announce(state, variant, previousSize) {
    if (!this.$.status) return;
    if (!variant) {
      this.#status(`${state.label} — not available to order.`, 'error');
      return;
    }
    let line = `${state.label} — ${variant.price}`;
    if (previousSize && variant.size && variant.size !== previousSize) {
      line += ` — size ${variant.size}`;
    }
    if (!variant.available) line += ' — sold out';
    this.#status(line);
  }

  #status(text, tone) {
    if (!this.$.status) return;
    this.$.status.textContent = text || '';
    if (tone) this.$.status.dataset.tone = tone;
    else delete this.$.status.dataset.tone;
  }

  #refreshCta(state, variant, _previousSize) {
    const usable = Boolean(variant && variant.available);
    this.#setSlideEnabled(usable);
    if (!this.$.slideText) return;
    if (!variant) this.$.slideText.textContent = 'NOT AVAILABLE';
    else if (!variant.available) this.$.slideText.textContent = 'SOLD OUT';
    else this.$.slideText.textContent = this.#posterOnly ? 'ADD TO CART' : 'SLIDE TO ADD';
  }

  #setSlideEnabled(enabled) {
    if (!this.$.slide) return;
    const blocked = !enabled || this.#state === 'animating' || this.#addInFlight;
    this.$.slide.dataset.disabled = String(blocked);
    if (this.$.handle) this.$.handle.disabled = blocked;
  }

  #setState(next) {
    this.#state = next;
    this.dataset.state = next;
    if (next === 'animating') this.#setSlideEnabled(false);
    if (next === 'settled') {
      this.#setSlideEnabled(Boolean(this.#variant && this.#variant.available));
    }
  }

  /* ------------------------------------------------------------ keyboard */

  #onSwatchKeys = (e) => {
    // Base the step on the latest requested colour, so held-down arrow keys
    // keep advancing instead of fighting an in-flight transition.
    const from = this.#desired;
    const map = {
      ArrowLeft: from - 1,
      ArrowRight: from + 1,
      ArrowUp: from - 1,
      ArrowDown: from + 1,
      Home: 0,
      End: this.#states.length - 1,
    };
    if (e.key === ' ' || e.key === 'Enter') {
      const el = e.target.closest?.('[data-veyr-abc-swatch]');
      if (!el) return;
      e.preventDefault();
      this.goTo(Number(el.dataset.index));
      return;
    }
    if (!(e.key in map)) return;
    e.preventDefault();
    const target = clamp(map[e.key], 0, this.#states.length - 1);
    this.goTo(target);
    this.#focusSwatch(target);
  };

  /** Roving tabindex must follow focus immediately, not only on landing. */
  #focusSwatch(target) {
    this.$.swatches.forEach((sw, i) => {
      sw.tabIndex = i === target ? 0 : -1;
    });
    this.$.swatches[target]?.focus();
  }

  #onSizeKeys = (e) => {
    if (e.key === ' ' || e.key === 'Enter') {
      const el = e.target.closest?.('[data-veyr-abc-size]');
      if (!el) return;
      e.preventDefault();
      this.#selectSize(el.dataset.value);
      return;
    }
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const items = this.$.sizes.filter((b) => b.getAttribute('aria-disabled') !== 'true');
    if (!items.length) return;
    const at = items.indexOf(document.activeElement);
    let next = at;
    if (e.key === 'ArrowLeft') next = at <= 0 ? items.length - 1 : at - 1;
    if (e.key === 'ArrowRight') next = at >= items.length - 1 ? 0 : at + 1;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = items.length - 1;
    e.preventDefault();
    items[next]?.focus();
    this.#selectSize(items[next]?.dataset.value);
  };

  /* --------------------------------------------------------------- swipe */

  #onSwipeStart = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.#swipe = { x: e.clientX, y: e.clientY, axis: null, id: e.pointerId };
  };

  #onSwipeMove = (e) => {
    const s = this.#swipe;
    if (!s || e.pointerId !== s.id) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (!s.axis && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
      // Vertical intent belongs to the page; touch-action: pan-y keeps it there.
      s.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
    }
    if (s.axis === 'x') s.dx = dx;
  };

  #onSwipeEnd = (e) => {
    const s = this.#swipe;
    this.#swipe = null;
    if (!s || e.pointerId !== s.id || s.axis !== 'x') return;
    const dx = s.dx ?? 0;
    if (Math.abs(dx) < SWIPE_DISTANCE) return;
    this.goTo(dx < 0 ? this.#desired + 1 : this.#desired - 1);
  };

  #onSwipeCancel = () => {
    this.#swipe = null;
  };

  /* -------------------------------------------------------- slide to add */

  #measureTravel() {
    if (!this.$.track || !this.$.handle) return 0;
    const style = getComputedStyle(this.$.track);
    const pad = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    return Math.max(0, this.$.track.clientWidth - pad - this.$.handle.offsetWidth);
  }

  #paintSlide(progress) {
    this.#slideProgress = progress;
    // Measured once per drag; re-measured for programmatic paints only.
    if (!this.#slideDragging) this.#travelCache = this.#measureTravel();
    this.style.setProperty('--veyr-abc-handle-x', `${progress * this.#travelCache}px`);
    this.style.setProperty('--veyr-abc-progress', String(progress));
    if (this.$.slideText) this.$.slideText.style.opacity = String(1 - progress * 1.15);
  }

  #onSlideStart = (e) => {
    if (this.#posterOnly || this.$.handle?.disabled || this.#addInFlight) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    this.#travelCache = this.#measureTravel();
    this.#slideDragging = true;
    this.#didDrag = false;
    this.dataset.slideAnimating = 'false';
    try {
      this.$.handle.setPointerCapture?.(e.pointerId);
    } catch {
      /* capture is an optimisation, not a requirement */
    }
    this.#slideOrigin = e.clientX;
  };

  #onSlideMove = (e) => {
    if (!this.#slideDragging) return;
    const dx = e.clientX - this.#slideOrigin;
    if (Math.abs(dx) > 4) this.#didDrag = true;
    const max = this.#travelCache;
    this.#paintSlide(max ? clamp(dx / max, 0, 1) : 0);
  };

  #endDrag() {
    this.#slideDragging = false;
    // Time-boxed rather than a sticky flag: a drag that ends without emitting a
    // click must never swallow a later keyboard or tap activation.
    if (this.#didDrag) this.#dragEndedAt = performance.now();
    this.#didDrag = false;
    this.dataset.slideAnimating = 'true';
  }

  #onSlideEnd = (e) => {
    if (!this.#slideDragging) return;
    try {
      this.$.handle.releasePointerCapture?.(e.pointerId);
    } catch {
      /* already released */
    }
    const reached = this.#slideProgress >= SLIDE_THRESHOLD;
    this.#endDrag();
    if (reached) {
      this.#paintSlide(1);
      this.#add();
    } else {
      this.#paintSlide(0);
    }
  };

  #onSlideCancel = () => {
    if (!this.#slideDragging) return;
    this.#endDrag();
    this.#paintSlide(0);
  };

  #onSlideClick = (e) => {
    e.preventDefault();
    // The click that closes a drag is ignored; the drag already decided.
    if (performance.now() - this.#dragEndedAt < 400) return;
    this.dataset.slideAnimating = 'true';
    this.#paintSlide(1);
    this.#add();
  };

  /* ---------------------------------------------------------------- cart */

  #add() {
    if (this.#addInFlight) return;
    if (this.#state === 'animating') return;
    const variant = this.#variant;
    if (!variant || !variant.available) {
      this.#status('Choose an available colour and size first.', 'error');
      this.#paintSlide(0);
      return;
    }

    const form = this.$.form?.querySelector('form');
    const button = this.$.form?.querySelector('button[ref="addToCartButton"]');
    if (!form || !button) {
      this.#status('Add to cart is unavailable right now.', 'error');
      this.#paintSlide(0);
      return;
    }

    this.#addInFlight = true;
    this.#setState('adding');
    this.$.slide.dataset.state = 'adding';
    if (this.$.slideText) {
      this.$.slideText.textContent = 'ADDING…';
      this.$.slideText.style.opacity = '1';
    }
    this.#setSlideEnabled(false);
    this.#status('');

    if (this.$.variantInput) this.$.variantInput.value = String(variant.id);

    clearTimeout(this.#watchdog);
    this.#watchdog = window.setTimeout(() => {
      this.#addFailed('Could not reach the cart. Please try again.');
    }, ADD_WATCHDOG);

    // requestSubmit keeps the theme's submit pipeline while skipping the
    // button's own click animation, which would fire from a hidden element.
    if (typeof form.requestSubmit === 'function') form.requestSubmit(button);
    else button.click();
  }

  #onCartUpdate = (e) => {
    if (!this.#addInFlight) return;
    if (e.detail?.data?.source !== 'product-form-component') return;
    if (e.detail?.data?.didError) {
      this.#addFailed();
      return;
    }
    this.#addSucceeded();
  };

  #onCartError = (e) => {
    if (!this.#addInFlight) return;
    const message = e.detail?.data?.message;
    this.#addFailed(typeof message === 'string' ? message : undefined);
  };

  #addSucceeded() {
    clearTimeout(this.#watchdog);
    this.#addInFlight = false;
    this.#setState('success');
    this.$.slide.dataset.state = 'success';
    if (this.$.slideText) this.$.slideText.textContent = 'ADDED';
    this.#status(`${this.#states[this.#index]?.label ?? ''} added to your cart.`);
    this.#scheduleSlideReset();
  }

  #addFailed(message) {
    clearTimeout(this.#watchdog);
    this.#addInFlight = false;
    this.#setState('error');
    this.$.slide.dataset.state = 'error';
    if (this.$.slideText) this.$.slideText.textContent = 'TRY AGAIN';
    this.#status(message || 'That did not go through. Please try again.', 'error');
    this.#scheduleSlideReset();
  }

  #scheduleSlideReset() {
    clearTimeout(this.#resetTimer);
    this.#resetTimer = window.setTimeout(() => {
      this.dataset.slideAnimating = 'true';
      this.#paintSlide(0);
      delete this.$.slide.dataset.state;
      this.#setState('settled');
      this.#refreshCta(this.#states[this.#index], this.#variant);
    }, RESET_DELAY);
  }
}

if (!customElements.get('veyr-aurelis-ball-court')) {
  customElements.define('veyr-aurelis-ball-court', VeyrAurelisBallCourt);
}
